<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import OverflowMenu from '@/components/shared/OverflowMenu.vue'
import PaginationControls from '@/components/shared/PaginationControls.vue'
import CountryFlag from '@/components/shared/CountryFlag.vue'
import PerPageSelect from '@/components/shared/PerPageSelect.vue'
import { useParticipants } from '@/composables/useParticipants'
import type { Participant, RegistrationStatus, PaginationMeta } from '@/types/participants'

const props = defineProps<{
  participants: Participant[]
  paginationMeta: PaginationMeta | null
  loading: boolean
  showRaceColumn: boolean
  onBulkPromote?: (ids: number[]) => Promise<void>
  eventSlug?: string
  editionSlug?: string
}>()

const emit = defineEmits<{
  filter: [filters: { status: RegistrationStatus | null; search: string; page: number; per_page: number }]
}>()

const { t, locale } = useI18n()
const router = useRouter()

const searchInput = ref('')
const selectedStatus = ref<RegistrationStatus | null>(null)
const currentPage = ref(1)
const perPage = ref(25)

let debounceTimer: ReturnType<typeof setTimeout> | null = null

const statusOptions: Array<{ value: RegistrationStatus; labelKey: string }> = [
  { value: 'payment_pending', labelKey: 'participants.status.payment_pending' },
  { value: 'completed', labelKey: 'participants.status.completed' },
  { value: 'cancelled', labelKey: 'participants.status.cancelled' },
  { value: 'payment_expired', labelKey: 'participants.status.payment_expired' },
  { value: 'waiting_list', labelKey: 'participants.status.waiting_list' },
]

function emitFilters(): void {
  emit('filter', {
    status: selectedStatus.value,
    search: searchInput.value,
    page: currentPage.value,
    per_page: perPage.value,
  })
}

function onSearchInput(): void {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    currentPage.value = 1
    emitFilters()
  }, 300)
}

function onStatusChange(): void {
  currentPage.value = 1
  emitFilters()
}

function onPerPageChange(value: number): void {
  perPage.value = value
  currentPage.value = 1
  emitFilters()
}

function goToPage(page: number): void {
  currentPage.value = page
  emitFilters()
}

function goToDetails(participantId: number): void {
  void router.push({
    name: 'organizer.registrations.edit',
    params: { registrationId: participantId },
  })
}

function formatCurrency(value: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(
    parseFloat(value),
  )
}

function formatShortDate(dateString: string): string {
  return new Intl.DateTimeFormat(locale.value, { dateStyle: 'short' }).format(new Date(dateString))
}

function formatFullDateTime(dateString: string): string {
  return new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(dateString),
  )
}

function genderIcon(gender: string | null): string {
  switch (gender) {
    case 'male':
      return '\u2642'
    case 'female':
      return '\u2640'
    case 'non_binary':
      return '\u26A7'
    default:
      return '\u2014'
  }
}

function statusBadgeClass(status: RegistrationStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
    case 'payment_pending':
      return 'bg-amber-500/15 border-amber-500/30 text-amber-400'
    case 'cancelled':
      return 'bg-red-500/15 border-red-500/30 text-red-400'
    case 'payment_expired':
      return 'bg-slate-500/15 border-slate-500/30 text-slate-300'
    case 'waiting_list':
      return 'bg-blue-500/15 border-blue-500/30 text-blue-400'
    default:
      return 'bg-slate-700 border-slate-600 text-slate-400'
  }
}

const totalPages = computed(() => props.paginationMeta?.last_page ?? 1)
const total = computed(() => props.paginationMeta?.total ?? 0)

watch(
  () => props.paginationMeta?.current_page,
  (val) => {
    if (val) currentPage.value = val
  },
)

// Bulk promote state
const selectedIds = ref<Set<number>>(new Set())
const bulkPromoting = ref(false)
const bulkPromoteError = ref<string | null>(null)

const waitingListParticipants = computed(() =>
  props.participants.filter((p) => p.status === 'waiting_list'),
)

const allWaitingListSelected = computed(() =>
  waitingListParticipants.value.length > 0 &&
  waitingListParticipants.value.every((p) => selectedIds.value.has(p.id)),
)

function toggleSelectAll(): void {
  if (allWaitingListSelected.value) {
    waitingListParticipants.value.forEach((p) => selectedIds.value.delete(p.id))
  } else {
    waitingListParticipants.value.forEach((p) => selectedIds.value.add(p.id))
  }
  // Trigger reactivity
  selectedIds.value = new Set(selectedIds.value)
}

function toggleSelect(id: number): void {
  if (selectedIds.value.has(id)) {
    selectedIds.value.delete(id)
  } else {
    selectedIds.value.add(id)
  }
  selectedIds.value = new Set(selectedIds.value)
}

async function handleBulkPromote(): Promise<void> {
  if (!props.onBulkPromote || selectedIds.value.size === 0) return
  bulkPromoting.value = true
  bulkPromoteError.value = null
  try {
    await props.onBulkPromote(Array.from(selectedIds.value))
    selectedIds.value = new Set()
  } catch {
    bulkPromoteError.value = 'common.error_generic'
  } finally {
    bulkPromoting.value = false
  }
}

// Clear selection when participants list changes
watch(
  () => props.participants,
  () => {
    selectedIds.value = new Set()
  },
)

// Resend confirmation email
const { resendConfirmationEmail } = useParticipants()

const resendingId = ref<number | null>(null)
const resendSuccessMessage = ref('')
const resendErrorMessage = ref('')

function isAxiosError(
  e: unknown,
): e is { response: { status: number; data: { message?: string; errors?: Record<string, string[]> } } } {
  return typeof e === 'object' && e !== null && 'response' in e
}

async function handleResendConfirmation(participantId: number): Promise<void> {
  if (resendingId.value !== null) return

  resendingId.value = participantId
  resendSuccessMessage.value = ''
  resendErrorMessage.value = ''

  try {
    await resendConfirmationEmail(participantId)
    resendSuccessMessage.value = t('participants.resend_confirmation.success')
  } catch (e) {
    if (isAxiosError(e) && e.response.status === 422) {
      resendErrorMessage.value =
        e.response.data.errors?.['status']?.[0]
        ?? e.response.data.errors?.['participant']?.[0]
        ?? t('participants.resend_confirmation.error')
    } else if (isAxiosError(e) && e.response.status === 403) {
      resendErrorMessage.value = t('participants.resend_confirmation.forbidden')
    } else {
      resendErrorMessage.value = t('participants.resend_confirmation.error')
    }
  } finally {
    resendingId.value = null
  }
}
</script>

<template>
  <div>
    <!-- Bulk promote bar (only when onBulkPromote prop is provided and items are selected) -->
    <div
      v-if="onBulkPromote && selectedIds.size > 0"
      class="flex items-center gap-3 mb-4 bg-blue-500/10 border border-blue-500/30 rounded-xl px-3 py-3"
    >
      <span class="text-sm text-blue-300 flex-1">
        {{ selectedIds.size }} {{ selectedIds.size === 1 ? t('participants.waiting_list.selected_singular') : t('participants.waiting_list.selected_plural') }}
      </span>
      <div v-if="bulkPromoteError" class="text-sm text-red-400">
        {{ t(bulkPromoteError) }}
      </div>
      <button
        type="button"
        :disabled="bulkPromoting"
        class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        @click="handleBulkPromote"
      >
        <AppSpinner v-if="bulkPromoting" size="button" />
        {{ t('participants.waiting_list.promote_selected') }}
      </button>
    </div>

    <!-- Resend confirmation feedback banners -->
    <div
      v-if="resendSuccessMessage"
      class="mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
      role="status"
    >
      {{ resendSuccessMessage }}
    </div>
    <div
      v-if="resendErrorMessage"
      class="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      role="alert"
    >
      {{ resendErrorMessage }}
    </div>

    <!-- Filter bar -->
    <div class="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
      <div class="relative flex-1 w-full sm:max-w-xs">
        <input
          v-model="searchInput"
          type="text"
          :placeholder="t('participants.search_placeholder')"
          class="w-full rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          @input="onSearchInput"
        />
      </div>

      <select
        v-model="selectedStatus"
        class="rounded-lg bg-slate-800 border border-slate-700 text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        @change="onStatusChange"
      >
        <option :value="null">{{ t('participants.filter_status') }}</option>
        <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
          {{ t(opt.labelKey) }}
        </option>
      </select>

      <PerPageSelect :model-value="perPage" @update:model-value="onPerPageChange" />

      <span class="text-sm text-slate-400 sm:ml-auto">{{ t('participants.participant_count', { count: total }) }}</span>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <svg class="animate-spin h-8 w-8 text-emerald-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="participants.length === 0"
      class="rounded-xl border border-dashed border-slate-700 px-8 py-12 text-center"
    >
      <p class="text-slate-400 text-sm">{{ t('participants.empty_state') }}</p>
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead>
          <tr class="border-b border-slate-700">
            <!-- Checkbox column header (only when bulk promote is enabled and waiting list participants exist) -->
            <th v-if="onBulkPromote && waitingListParticipants.length > 0" class="px-3 py-3 w-10">
              <input
                type="checkbox"
                :checked="allWaitingListSelected"
                class="rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-900"
                @change="toggleSelectAll"
              />
            </th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('participants.table.name') }}
            </th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('participants.table.gender') }}
            </th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('participants.table.email') }}
            </th>
            <th
              v-if="showRaceColumn"
              class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider"
            >
              {{ t('participants.table.race') }}
            </th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('participants.table.status') }}
            </th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider text-right">
              {{ t('participants.table.revenue') }}
            </th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('participants.table.registered_at') }}
            </th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('participants.table.actions') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="p in participants"
            :key="p.id"
            class="border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors"
          >
            <!-- Checkbox cell (only for waiting list entries when bulk promote is enabled) -->
            <td v-if="onBulkPromote && waitingListParticipants.length > 0" class="px-3 py-3 w-10">
              <input
                v-if="p.status === 'waiting_list'"
                type="checkbox"
                :checked="selectedIds.has(p.id)"
                class="rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-900"
                @change="toggleSelect(p.id)"
              />
            </td>
            <td class="px-3 py-3 text-white font-medium">
              <CountryFlag :code="p.user.nationality" />{{ p.user.first_name }} {{ p.user.last_name }}
              <span
                v-if="p.has_remarks"
                class="ml-2 inline-flex align-middle text-amber-400"
                role="img"
                :aria-label="t('participants.table.has_remarks')"
                :title="t('participants.table.has_remarks')"
                data-testid="remarks-indicator"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8 10h8M8 14h5m-9 6l2.5-3H18a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v14z" />
                </svg>
              </span>
            </td>
            <td class="px-3 py-3 text-slate-300 text-lg" :title="p.user.gender ?? ''">
              {{ genderIcon(p.user.gender) }}
            </td>
            <td class="px-3 py-3 text-slate-300">{{ p.user.email }}</td>
            <td v-if="showRaceColumn" class="px-3 py-3">
              <RouterLink
                v-if="eventSlug && editionSlug"
                :to="`/organizer/events/${eventSlug}/editions/${editionSlug}/races/${p.race.slug}/participants`"
                class="text-slate-300 hover:text-emerald-400 transition-colors"
              >
                {{ p.race.name }}
              </RouterLink>
              <span v-else class="text-slate-300">{{ p.race.name }}</span>
            </td>
            <td class="px-3 py-3">
              <span
                :class="statusBadgeClass(p.status)"
                class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
              >
                {{ t(`participants.status.${p.status}`) }}
              </span>
            </td>
            <td class="px-3 py-3 text-slate-300 text-right">{{ formatCurrency(p.amount_paid) }}</td>
            <td class="px-3 py-3 text-slate-400 text-xs" :title="formatFullDateTime(p.registered_at)">
              {{ formatShortDate(p.registered_at) }}
            </td>
            <td class="px-3 py-3">
              <div class="flex items-center justify-end gap-2">
                <button
                  type="button"
                  class="text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors px-3 py-1.5 rounded-lg border border-emerald-500/30 hover:border-emerald-500/60"
                  :class="{ 'hidden sm:inline-flex': p.status === 'completed' }"
                  @click="goToDetails(p.id)"
                >
                  {{ t('participants.table.details_button') }}
                </button>

                <OverflowMenu
                  teleport
                  menu-class="w-56"
                  :disabled="p.status !== 'completed'"
                  :aria-label="p.status === 'completed'
                    ? t('participants.table.row_actions')
                    : t('participants.table.no_row_actions')"
                >
                  <!-- Mobile-only: below sm, the Details button above is hidden for
                       rows with an enabled menu (completed rows), so it is
                       duplicated here (sm:hidden) to keep it reachable. Rows with a
                       disabled menu keep the inline Details button visible. -->
                  <button
                    type="button"
                    class="sm:hidden w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
                    @click="goToDetails(p.id)"
                  >
                    {{ t('participants.table.details_button') }}
                  </button>
                  <hr class="sm:hidden border-slate-700 my-1" />
                  <button
                    type="button"
                    :disabled="resendingId !== null"
                    class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    @click="handleResendConfirmation(p.id)"
                  >
                    {{ resendingId === p.id
                      ? t('participants.resend_confirmation.sending')
                      : t('participants.resend_confirmation.action') }}
                  </button>
                </OverflowMenu>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <PaginationControls :current-page="currentPage" :total-pages="totalPages" @change="goToPage" />
    </div>
  </div>
</template>
